import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductionTreeView.css';
import { getApiUrl, apiFetch } from '../../config/api';
import TaskExecutionDetailModal from '../../components/Tasks/TaskExecutionDetailModal';

const ProductionTreeView = React.forwardRef((props, ref) => {
  const { t } = useTranslation('production');
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailTask, setDetailTask] = useState(null); // Task selected for drill-in modal

  // Expose refresh method to parent components
  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (selectedPO) {
        fetchTreeData(selectedPO);
      } else {
        fetchProductionOrders();
      }
    }
  }));

  // Fetch production orders on mount
  useEffect(() => {
    fetchProductionOrders();
  }, []);

  // Fetch tree data when a PO is selected
  useEffect(() => {
    if (selectedPO) {
      fetchTreeData(selectedPO);
      // Collapse all nodes when loading new tree data
      setExpandedNodes(new Set());
    }
  }, [selectedPO]);

  const fetchProductionOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(getApiUrl('/api/production/orders?status=RELEASED'));
      if (response.ok) {
        const data = await response.json();
        const orders = data.orders || data;
        setProductionOrders(orders);
        // Auto-select first order if available
        if (orders.length > 0 && !selectedPO) {
          setSelectedPO(orders[0].po_number);
        } else if (orders.length === 0) {
          // Clear any existing selection and tree data when no orders exist
          setSelectedPO(null);
          setTreeData(null);
        }
      } else {
        setError(t('productionTreeView.errors.fetchOrdersFailed'));
      }
    } catch (err) {
      console.error('Error fetching production orders:', err);
      setError(t('productionTreeView.errors.loadOrdersError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTreeData = async (poNumber) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch comprehensive tree data from new endpoint
      const response = await apiFetch(getApiUrl(`/api/production/orders/${poNumber}/tree-data`));
      if (!response.ok) {
        throw new Error(t('productionTreeView.errors.fetchTreeFailed'));
      }
      const data = await response.json();

      // Build tree structure from comprehensive data
      const tree = buildEnhancedTreeStructure(data);
      setTreeData(tree);
    } catch (err) {
      console.error('Error fetching tree data:', err);
      setError(t('productionTreeView.errors.loadTreeError', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const buildEnhancedTreeStructure = (data) => {
    const { order, lines } = data;

    const tree = {
      id: `order-${order.production_order_id}`,
      type: 'order',
      label: `${order.po_number} - ${order.customer_name || t('productionTreeView.unknownCustomer')}`,
      status: order.status,
      metadata: {
        planned_start: order.planned_start_date,
        planned_due: order.planned_due_date,
        total_amount: order.total_amount,
        priority: order.priority
      },
      children: []
    };

    // Process each production order line
    for (const line of lines) {
      // Calculate completion percentage
      let completion = 0;
      if (line.produced_quantity && line.order_quantity) {
        completion = Math.round((line.produced_quantity / line.order_quantity) * 100);
      }

      const lineNode = {
        id: `line-${line.production_order_line_id}`,
        type: 'line',
        label: t('productionTreeView.lineLabel', { number: line.line_number, product: line.product_name }),
        status: line.status,
        metadata: {
          product_code: line.product_code,
          quantity: line.order_quantity,
          produced: line.produced_quantity,
          completion: completion,
          material_cost: line.material_cost,
          waste_cost: line.waste_cost,
          total_cost: line.total_cost
        },
        children: []
      };

      // Add product node
      const productNode = {
        id: `product-${line.product_id}`,
        type: 'product',
        label: line.product_name,
        metadata: {
          product_code: line.product_code
        },
        children: []
      };

      // Add BOM with materials if available
      if (line.bom_items && line.bom_items.length > 0) {
        const bomNode = {
          id: `bom-${line.bom_id}`,
          type: 'bom',
          label: t('productionTreeView.bomLabel', { count: line.bom_items.length }),
          metadata: {},
          children: []
        };

        // Add BOM materials with costs
        for (const item of line.bom_items) {
          bomNode.children.push({
            id: `bom-item-${item.bom_item_id}`,
            type: 'bom_item',
            label: `${item.item_code} - ${item.item_name}`,
            metadata: {
              quantity: item.quantity_per_unit,
              unit: item.unit_of_measure,
              unit_cost: item.unit_cost,
              total_cost: item.total_cost,
              item_type: item.item_type
            }
          });
        }

        productNode.children.push(bomNode);
      }

      // Add material requisitions if available
      if (line.requisitions && line.requisitions.length > 0) {
        const reqNode = {
          id: `requisitions-${line.production_order_line_id}`,
          type: 'requisitions',
          label: t('productionTreeView.requisitionsLabel', { count: line.requisitions.length }),
          metadata: {},
          children: []
        };

        for (const req of line.requisitions) {
          const issuedPct = req.total_requested > 0
            ? Math.round((req.total_issued / req.total_requested) * 100)
            : 0;

          reqNode.children.push({
            id: `req-${req.requisition_id}`,
            type: 'requisition',
            label: req.requisition_number,
            status: req.status,
            metadata: {
              requested: req.total_requested,
              issued: req.total_issued,
              issued_percentage: issuedPct,
              item_count: req.item_count,
              priority: req.priority
            }
          });
        }

        productNode.children.push(reqNode);
      }

      // Add workflow with instantiated tasks
      if (line.tasks && line.tasks.length > 0) {
        const workflowNode = {
          id: `workflow-${line.workflow_id}-line-${line.production_order_line_id}`,
          type: 'workflow',
          label: line.workflow_name || t('productionTreeView.productionWorkflow'),
          metadata: {
            total_tasks: line.tasks.length,
            completed_tasks: line.tasks.filter(t => t.status === 'COMPLETED').length
          },
          children: []
        };

        // Add actual instantiated tasks with status and progress
        for (const task of line.tasks) {
          const taskNode = {
            id: `task-${task.task_id}`,
            type: 'task',
            label: task.task_name,
            status: task.status,
            // Store fields needed by TaskExecutionDetailModal directly on the node
            task_id: task.task_id,
            task_type: task.task_type,
            task_name: task.task_name,
            task_code: task.task_code,
            production_order_id: order.production_order_id,
            metadata: {
              task_code: task.task_code,
              sequence: task.task_sequence,
              estimated_duration: task.estimated_duration_minutes,
              actual_duration: task.actual_duration_minutes,
              logged_minutes: task.logged_minutes,
              completion_percentage: task.completion_percentage,
              department: task.department_name,
              machine: task.machine_name,
              priority: task.priority,
              scheduled_start: task.scheduled_start,
              scheduled_end: task.scheduled_end,
              actual_start: task.actual_start,
              actual_end: task.actual_end,
              assigned_user: task.assigned_user_name
            }
          };

          workflowNode.children.push(taskNode);
        }

        productNode.children.push(workflowNode);
      }

      lineNode.children.push(productNode);
      tree.children.push(lineNode);
    }

    return tree;
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (!treeData) return;
    const allNodeIds = new Set();
    const collectNodeIds = (node) => {
      allNodeIds.add(node.id);
      if (node.children) {
        node.children.forEach(child => collectNodeIds(child));
      }
    };
    collectNodeIds(treeData);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'PENDING': '#FFA500',
      'IN_PROGRESS': '#2196F3',
      'COMPLETED': '#4CAF50',
      'CANCELLED': '#F44336',
      'RELEASED': '#2196F3',
      'Draft': '#9E9E9E'
    };
    return statusColors[status] || '#9E9E9E';
  };

  const getNodeIcon = (type) => {
    const icons = {
      order: '📦',
      line: '📋',
      product: '🏷️',
      bom: '📝',
      bom_item: '🔧',
      product_type: '📐',
      workflow: '⚙️',
      task: '✓',
      requisitions: '📑',
      requisition: '📄'
    };
    return icons[type] || '•';
  };

  const formatCurrency = (value) => {
    if (value == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const renderTreeNode = (node, level = 0) => {
    if (!node) return null;

    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const indent = level * 24;

    return (
      <div key={node.id} className="tree-node-container">
        <div
          className={`tree-node tree-node-${node.type}${node.type === 'task' ? ' tree-node-task-clickable' : ''}`}
          style={{ paddingLeft: `${indent}px`, cursor: node.type === 'task' ? 'pointer' : undefined }}
          onClick={node.type === 'task' ? (e) => { e.stopPropagation(); setDetailTask(node); } : undefined}
          title={node.type === 'task' ? t('productionTreeView.clickToViewDetail') : undefined}
        >
          {hasChildren && (
            <button
              className="expand-button"
              onClick={() => toggleNode(node.id)}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span className="expand-spacer"></span>}

          <span className="node-icon">{getNodeIcon(node.type)}</span>
          <span className="node-label">{node.label}</span>

          {/* Status badge */}
          {node.status && (
            <span
              className="node-status"
              style={{ backgroundColor: getStatusColor(node.status) }}
            >
              {node.status}
            </span>
          )}

          {/* Line completion and costs */}
          {node.type === 'line' && node.metadata && (
            <>
              {node.metadata.completion !== undefined && (
                <span className="node-progress">
                  {t('productionTreeView.percentComplete', { percent: node.metadata.completion })}
                </span>
              )}
              {node.metadata.total_cost > 0 && (
                <span className="node-cost">
                  💰 {formatCurrency(node.metadata.total_cost)}
                </span>
              )}
              <span className="node-quantity">
                {t('productionTreeView.qty')}: {node.metadata.produced || 0}/{node.metadata.quantity}
              </span>
            </>
          )}

          {/* BOM item with costs */}
          {node.type === 'bom_item' && node.metadata && (
            <>
              <span className="node-quantity">
                {node.metadata.quantity} {node.metadata.unit || t('productionTreeView.units')}
              </span>
              {node.metadata.unit_cost > 0 && (
                <span className="node-cost">
                  @ {formatCurrency(node.metadata.unit_cost)}
                </span>
              )}
            </>
          )}

          {/* Requisition progress */}
          {node.type === 'requisition' && node.metadata && (
            <>
              <span className="node-quantity">
                {t('productionTreeView.issued', { issued: node.metadata.issued || 0, requested: node.metadata.requested || 0 })}
              </span>
              {node.metadata.issued_percentage !== undefined && (
                <span className="node-progress">
                  {node.metadata.issued_percentage}%
                </span>
              )}
            </>
          )}

          {/* Task drill-in icon */}
          {node.type === 'task' && (
            <span className="task-detail-hint" title={t('productionTreeView.viewExecutionDetail')}>👁</span>
          )}

          {/* Task progress and duration */}
          {node.type === 'task' && node.metadata && (
            <>
              {node.metadata.completion_percentage !== undefined && (
                <span className="node-progress-bar">
                  <span
                    className="progress-fill"
                    style={{
                      width: `${node.metadata.completion_percentage}%`,
                      backgroundColor: node.metadata.completion_percentage === 100 ? '#4CAF50' : '#2196F3'
                    }}
                  />
                  <span className="progress-text">{node.metadata.completion_percentage}%</span>
                </span>
              )}
              {node.metadata.logged_minutes !== undefined && node.metadata.estimated_duration && (
                <span className="node-time">
                  ⏱ {formatDuration(node.metadata.logged_minutes)}/{formatDuration(node.metadata.estimated_duration)}
                </span>
              )}
              {node.metadata.assigned_user && (
                <span className="node-assignments">
                  👤 {node.metadata.assigned_user}
                </span>
              )}
              {node.metadata.department && (
                <span className="node-department">
                  {node.metadata.department}
                </span>
              )}
            </>
          )}

          {/* Workflow progress */}
          {node.type === 'workflow' && node.metadata && (
            <span className="node-quantity">
              {t('productionTreeView.tasksProgress', { completed: node.metadata.completed_tasks || 0, total: node.metadata.total_tasks || 0 })}
            </span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="tree-node-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading && !treeData) {
    return (
      <div className="tree-view-loading">
        <div className="loading-spinner"></div>
        <p>{t('productionTreeView.loading')}</p>
      </div>
    );
  }

  return (
    <div className="tree-view-container">
      <div className="tree-view-header">
        <h2>📦 {t('productionTreeView.title')}</h2>

        <div className="tree-controls">
          <select
            className="po-selector"
            value={selectedPO || ''}
            onChange={(e) => setSelectedPO(e.target.value)}
          >
            <option value="" disabled hidden>{t('productionTreeView.selectOrder')}</option>
            {productionOrders.map(po => (
              <option key={po.production_order_id} value={po.po_number}>
                {po.po_number} - {po.customer_name || t('common:ui.unknown')}
              </option>
            ))}
          </select>

          <button className="btn-expand-all" onClick={expandAll}>
            {t('productionTreeView.expandAll')}
          </button>
          <button className="btn-collapse-all" onClick={collapseAll}>
            {t('productionTreeView.collapseAll')}
          </button>
          <button
            className="btn-refresh"
            onClick={() => selectedPO ? fetchTreeData(selectedPO) : fetchProductionOrders()}
            disabled={loading}
          >
            🔄 {t('common:ui.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="tree-error">
          <p>⚠️ {error}</p>
        </div>
      )}

      {!selectedPO && !loading && productionOrders.length > 0 && (
        <div className="tree-placeholder">
          <div className="selection-prompt">
            <h3>📦 {t('productionTreeView.title')}</h3>
            <p>{t('productionTreeView.selectPrompt')}</p>
          </div>
        </div>
      )}

      {selectedPO && treeData && (
        <div className="tree-view-content">
          {renderTreeNode(treeData)}
        </div>
      )}

      {selectedPO && !treeData && !loading && !error && (
        <div className="tree-placeholder">
          <p>{t('productionTreeView.noData')}</p>
        </div>
      )}

      {/* Task Execution Detail Modal */}
      {detailTask && (
        <TaskExecutionDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
        />
      )}
    </div>
  );
});

ProductionTreeView.displayName = 'ProductionTreeView';

export default ProductionTreeView;
